import React from 'react';
import { useSelector } from 'react-redux';
import { colors } from '@/globalStyles';
import { STROKE_DASHARRAY, STROKE_WIDTH } from '@/constants';
import { isDedicatedMaterial } from '@/helpers';
import LineComponent from '../../shared/PrimitiveRenderer/Line';

const getBorderStyles = (data) => {
  const { isMatrixBordersEnabled, ribbonColor, isShieldSelected } = data;
  const strokeWidth = STROKE_WIDTH;
  const strokeDasharray = STROKE_DASHARRAY;
  const color = isDedicatedMaterial() ? colors.white : null;

  if (isMatrixBordersEnabled) {
    if (isShieldSelected) {
      return { stroke: (color || colors.black), strokeWidth };
    }
    return { stroke: (color || ribbonColor), strokeWidth };
  } else if (isShieldSelected) {
    return { stroke: (color || colors.dimGray), strokeDasharray, strokeWidth };
  }
  return { stroke: (color || ribbonColor), strokeDasharray, strokeWidth };
};

const getSafeValue = value => Math.round(value * 100000) / 100000;

const isBorderIntersectToShield = (shield, border) => {
  const { w, h, y, x } = shield.primitiveProps;
  const [position, coords] = border;

  const safeShieldStartY = getSafeValue(y);
  const safeShieldEndY = getSafeValue(y + h);
  const safeShieldStartX = getSafeValue(x);
  const safeShieldEndX = getSafeValue(x + w);

  switch (position) {
    case 'top': {
      return coords.start.y === safeShieldStartY && coords.end.y === safeShieldStartY;
    }
    case 'bottom': {
      return coords.start.y === safeShieldEndY && coords.end.y === safeShieldEndY;
    }
    case 'left': {
      return coords.start.x === safeShieldStartX && coords.end.x === safeShieldStartX;
    }
    case 'right': {
      return coords.start.x === safeShieldEndX && coords.end.x === safeShieldEndX;
    }
    default: {
      return false;
    }
  }
};

const isBorderIntersectToSibling = (borders, primitiveProps) => {
  return borders.some(border => {
    return JSON.stringify(border.primitiveProps) === JSON.stringify(primitiveProps);
  });
};

const MatrixBorders = props => {
  const { sections, selectedShield, isMatrixShield, shieldsToRender } = props;
  const shieldId = selectedShield?.id;
  const ribbonColor = useSelector(state => state.core.ribbonColor);
  const shieldsSelection = useSelector(state => state.core.shieldsSelection);
  const isShieldSelected = shieldsSelection[shieldId];

  const borderLines = Object.values(sections).reduce((acc1, section) => {
    if (isMatrixShield[section.shieldId]) {
      if (section.type === 'separator') {
        return acc1;
      }

      const currentShield = shieldsToRender.find(shield => shield.id === section.shieldId);
      const isMatrixBordersEnabled = currentShield.isMatrixBordersEnabled;
      const safeStartX = getSafeValue(section.x);
      const safeStartY = getSafeValue(section.y);
      const safeEndX = getSafeValue(section.x + section.width);
      const safeEndY = getSafeValue(section.y + section.height);

      const square = {
        top: {
          start: { x: safeStartX, y: safeStartY },
          end: { x: safeEndX, y: safeStartY },
        },
        left: {
          start: { x: safeStartX, y: safeStartY },
          end: { x: safeStartX, y: safeEndY },
        },
        right: {
          start: { x: safeEndX, y: safeStartY },
          end: { x: safeEndX, y: safeEndY },
        },
        bottom: {
          start: { x: safeStartX, y: safeEndY },
          end: { x: safeEndX, y: safeEndY },
        },
      };

      const bordersToRender = Object.entries(square).reduce((acc2, border) => {
        const [position, coords] = border;

        const primitiveProps = {
          start_point: coords.start,
          end_point: coords.end,
          outline_style: {},
        };

        if (isBorderIntersectToShield(currentShield, border) || isBorderIntersectToSibling(acc2, primitiveProps)) {
          return acc2;
        }
        return [
          ...acc2,
          {
            position,
            section,
            primitiveProps,
            style: getBorderStyles({ isMatrixBordersEnabled, ribbonColor, isShieldSelected }),
          },
        ];
      }, []);

      return [...acc1, ...bordersToRender];
    }
    return acc1;
  }, []);

  return borderLines.map(({ position, section, primitiveProps, style }) => {
    return <LineComponent key={`line_${position}_${section.id}`} style={style} primitiveProps={primitiveProps} />;
  });
};

export default MatrixBorders;
